/**
 * Toggle microphone: click to record, click again to transcribe and fill target field / Froala.
 * Partial: application/voice_input — uses data-voice-* on [data-voice-input].
 */

const stateByEl = new WeakMap();

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

function pickMimeType() {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
  ];
  for (let i = 0; i < candidates.length; i++) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported(candidates[i])) {
      return candidates[i];
    }
  }
  return '';
}

function escapeHtml(text) {
  const d = document.createElement('div');
  d.textContent = text;
  return d.innerHTML;
}

function findFroalaForTarget(targetId) {
  if (!window.FroalaEditor || !Array.isArray(window.FroalaEditor.INSTANCES)) {
    return null;
  }
  const domEl = document.getElementById(targetId);
  const instances = window.FroalaEditor.INSTANCES;
  for (let i = 0; i < instances.length; i++) {
    const inst = instances[i];
    // Inline / iframe: visible surface often is not the node with the original id.
    // $oel is the original element (e.g. textarea#employee_kurzebeschreibung).
    const $oel = inst.$oel;
    const oelId = $oel && typeof $oel.attr === 'function' ? $oel.attr('id') : null;
    const oel = $oel && $oel[0];
    if (oelId === targetId || (oel && (oel.id === targetId || oel === domEl))) {
      return inst;
    }
    const el = inst.$el && inst.$el[0];
    if (el && (el.id === targetId || el === domEl)) {
      return inst;
    }
    if (inst.el && (inst.el.id === targetId || inst.el === domEl)) {
      return inst;
    }
  }
  return null;
}

function insertTranscript(targetId, text, append) {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    return;
  }

  const froala = findFroalaForTarget(targetId);
  if (froala) {
    const chunk = escapeHtml(trimmed) + ' ';
    try {
      if (froala.events && typeof froala.events.focus === 'function') {
        froala.events.focus();
      }
    } catch (e) { /* ignore */ }
    if (append) {
      froala.html.insert(chunk);
    } else {
      froala.html.set(chunk);
    }
    try {
      if (froala.events && typeof froala.events.trigger === 'function') {
        froala.events.trigger('contentChanged');
      }
    } catch (e) { /* ignore */ }
    return;
  }

  const el = document.getElementById(targetId);
  if (!el) {
    return;
  }

  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
    if (append) {
      const prefix = el.value && !/\s$/.test(el.value) ? ' ' : '';
      el.value = el.value + prefix + trimmed;
    } else {
      el.value = trimmed;
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

function setUiState(btn, state) {
  btn.classList.remove('voice-input--recording', 'voice-input--processing', 'voice-input--error');
  if (state === 'recording') {
    btn.classList.add('voice-input--recording');
    btn.setAttribute('aria-pressed', 'true');
  } else if (state === 'processing') {
    btn.classList.add('voice-input--processing');
    btn.setAttribute('aria-pressed', 'false');
  } else if (state === 'error') {
    btn.classList.add('voice-input--error');
    btn.setAttribute('aria-pressed', 'false');
  } else {
    btn.setAttribute('aria-pressed', 'false');
  }
}

async function startRecording(btn) {
  if (!navigator.mediaDevices || !window.MediaRecorder) {
    window.alert('Spracheingabe wird in diesem Browser nicht unterstützt.');
    return;
  }

  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    window.alert('Mikrofonzugriff wurde verweigert oder ist nicht verfügbar.');
    return;
  }

  const mimeType = pickMimeType();
  let recorder;
  try {
    recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);
  } catch (e) {
    stream.getTracks().forEach((t) => t.stop());
    window.alert('Audioaufnahme konnte nicht gestartet werden.');
    return;
  }

  const chunks = [];
  recorder.ondataavailable = (ev) => {
    if (ev.data && ev.data.size > 0) {
      chunks.push(ev.data);
    }
  };

  stateByEl.set(btn, {
    stream,
    recorder,
    chunks,
    mimeType: recorder.mimeType || mimeType || 'audio/webm',
  });

  recorder.start(200);
  setUiState(btn, 'recording');
}

async function stopAndTranscribe(btn) {
  const st = stateByEl.get(btn);
  if (!st || !st.recorder) {
    return;
  }

  const { stream, recorder, chunks, mimeType } = st;
  stateByEl.delete(btn);

  setUiState(btn, 'processing');

  await new Promise((resolve) => {
    recorder.onstop = resolve;
    try {
      recorder.stop();
    } catch (e) {
      resolve();
    }
    stream.getTracks().forEach((t) => t.stop());
  });

  const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
  if (!blob.size) {
    setUiState(btn, 'idle');
    return;
  }

  const url = btn.getAttribute('data-voice-url');
  if (!url) {
    setUiState(btn, 'error');
    return;
  }

  const ext = (mimeType && mimeType.indexOf('mp4') !== -1) ? 'm4a' : 'webm';
  const form = new FormData();
  form.append('audio', blob, `recording.${ext}`);
  const extraPrompt = btn.getAttribute('data-voice-initial-prompt');
  if (extraPrompt) {
    form.append('initial_prompt', extraPrompt);
  }

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      body: form,
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      credentials: 'same-origin',
    });
  } catch (e) {
    setUiState(btn, 'error');
    window.alert('Netzwerkfehler bei der Spracherkennung.');
    return;
  }

  let data;
  try {
    data = await res.json();
  } catch (e) {
    data = {};
  }

  if (!res.ok) {
    setUiState(btn, 'error');
    window.alert(data.error || 'Spracherkennung fehlgeschlagen.');
    return;
  }

  const targetId = btn.getAttribute('data-voice-target');
  const append = btn.getAttribute('data-voice-append') !== 'false';
  insertTranscript(targetId, data.text, append);

  setUiState(btn, 'idle');
}

function onVoiceInputActivate(btn) {
  if (btn.classList.contains('voice-input--processing')) {
    return;
  }

  const st = stateByEl.get(btn);
  if (st && st.recorder && st.recorder.state === 'recording') {
    void stopAndTranscribe(btn);
    return;
  }

  void startRecording(btn);
}

function onDocumentClick(ev) {
  const btn = ev.target.closest('[data-voice-input]');
  if (!btn) {
    return;
  }
  ev.preventDefault();
  onVoiceInputActivate(btn);
}

function onDocumentKeydown(ev) {
  if (ev.key !== 'Enter' && ev.key !== ' ') {
    return;
  }
  const btn = ev.target.closest('[data-voice-input]');
  if (!btn) {
    return;
  }
  ev.preventDefault();
  onVoiceInputActivate(btn);
}

let bound = false;

export function initVoiceInput() {
  if (bound) {
    return;
  }
  bound = true;
  document.addEventListener('click', onDocumentClick, true);
  document.addEventListener('keydown', onDocumentKeydown, true);
}
